import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import { AccordionSet } from '@folio/stripes/components';
import { stripesConnect } from '@folio/stripes/core';

import CheckboxFilter from '../../common/filters/checkbox-filter';
import UserFilter from '../../common/filters/user-filter';

import {
  STATUSES_FILTER_PARAMETER,
  USER_FILTER_PARAMETER,
  TASK_MODULE_NAME,
  TEAM_ASSIGNMENTS_FILTER_PARAMETER,
  TEAM_ASSIGNMENTS_FILTER_OPTIONS,
  TEAM_MODULE_NAME,
  LOAD_LIMIT_MAX,
} from '../../../constants';
import {
  applyFiltersAdapter,
  getCheckboxFilterOptions,
} from '../../../utils';

const TaskListFilters = ({
  activeFilters,
  applyFilters,
  taskStatuses,
  mutator,
}) => {
  const [teams, setTeams] = useState([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);

  const getTeams = useCallback(() => {
    setTeams([]);
    setTeamsLoaded(false);
    mutator.teams.GET({
      params: {
        limit: LOAD_LIMIT_MAX,
      },
    })
      .then((response) => {
        setTeams(response.teams);
        setTeamsLoaded(true);
      });
  }, []);

  useEffect(
    () => {
      getTeams();
    },
    [],
  );

  const adaptedApplyFilters = useCallback(
    applyFiltersAdapter(applyFilters),
    [applyFilters],
  );

  const getStatusDataOptions = useMemo(() => (
    getCheckboxFilterOptions(
      TASK_MODULE_NAME,
      STATUSES_FILTER_PARAMETER,
      taskStatuses,
    )), [taskStatuses]);

  const getTaskAssignmentDataOptions = () => (
    getCheckboxFilterOptions(
      TEAM_MODULE_NAME,
      TEAM_ASSIGNMENTS_FILTER_PARAMETER,
      TEAM_ASSIGNMENTS_FILTER_OPTIONS,
    ));

  const getTaskAssignmentsFilter = () => {
    if (teamsLoaded && teams.length) {
      return (
        <CheckboxFilter
          activeFilters={activeFilters[TEAM_ASSIGNMENTS_FILTER_PARAMETER]}
          id={TEAM_ASSIGNMENTS_FILTER_PARAMETER}
          labelId="ui-task-list.team.filter.title"
          name={TEAM_ASSIGNMENTS_FILTER_PARAMETER}
          options={getTaskAssignmentDataOptions()}
          onChange={adaptedApplyFilters}
        />
      );
    }

    return null;
  };

  return (
    <AccordionSet>
      <CheckboxFilter
        activeFilters={activeFilters[STATUSES_FILTER_PARAMETER]}
        id={STATUSES_FILTER_PARAMETER}
        labelId="ui-task-list.task.field.status"
        name={STATUSES_FILTER_PARAMETER}
        options={getStatusDataOptions}
        onChange={adaptedApplyFilters}
      />

      <UserFilter
        activeFilters={activeFilters[USER_FILTER_PARAMETER]}
        id={USER_FILTER_PARAMETER}
        labelId="ui-task-list.task.field.assignedUserName"
        name={USER_FILTER_PARAMETER}
        onChange={adaptedApplyFilters}
      />

      { getTaskAssignmentsFilter() }
    </AccordionSet>
  );
};

TaskListFilters.manifest = Object.freeze({
  teams: {
    type: 'okapi',
    path: 'tasks/teams',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: true,
    fetch: false
  },
});

TaskListFilters.propTypes = {
  activeFilters: PropTypes.object.isRequired,
  applyFilters: PropTypes.func.isRequired,
  taskStatuses: PropTypes.array.isRequired,
  mutator: PropTypes.shape({
    teams: PropTypes.shape({
      GET: PropTypes.func
    }),
  }),
};

export default stripesConnect(TaskListFilters);
